import React, { useState, useMemo, Fragment } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	FormControl,
	InputLabel,
	Select,
	OutlinedInput,
	MenuItem,
	Checkbox,
	ListItemText,
	Box,
	Typography, // Import Typography for better text styling
} from '@mui/material';

const LeaseComparisonTable = ({ leaseDataList, yoyData }) => {
	const [selectedYears, setSelectedYears] = useState([]);

	const availableYears = useMemo(() => {
		const allYears = yoyData.flatMap((lease) => lease.yoyData?.map((d) => d.year) || []);
		return [...new Set(allYears)].sort();
	}, [yoyData]);

	const displayData = useMemo(() => {
		if (selectedYears.length === 0) {
			return leaseDataList;
		}

		const sumYearlyData = (dataArray) => {
			if (!dataArray || dataArray.length === 0) return {};
			return dataArray.reduce((accumulator, currentYearData) => {
				for (const key in currentYearData) {
					if (key !== 'year' && typeof currentYearData[key] === 'number') {
						accumulator[key] = (accumulator[key] || 0) + currentYearData[key];
					}
				}
				return accumulator;
			}, {});
		};

		return yoyData.map((lease) => {
			const relevantYearlyData = lease.yoyData.filter((data) => selectedYears.includes(data.year));
			const aggregatedData = sumYearlyData(relevantYearlyData);

			return {
				leaseId: lease.leaseId,
				...aggregatedData,
			};
		});
	}, [leaseDataList, yoyData, selectedYears]);

	const allAccountTitles = useMemo(() => {
		const keys = new Set();
		leaseDataList.forEach((data) => Object.keys(data).forEach((key) => keys.add(key)));
		yoyData.forEach((lease) => {
			lease.yoyData.forEach((yearlyData) => {
				Object.keys(yearlyData).forEach((key) => keys.add(key));
			});
		});

		keys.delete('leaseId');
		keys.delete('yoyData');
		keys.delete('yoyYears');
		keys.delete('year');

		return Array.from(keys).sort();
	}, [leaseDataList, yoyData]);

	const handleYearChange = (event) => {
		const {
			target: { value },
		} = event;
		setSelectedYears(typeof value === 'string' ? value.split(',') : value);
	};

	// --- DESIGN IMPROVEMENTS START HERE ---

	return (
		<Fragment>
			{/* 1. A dedicated, styled container for the filter */}
			<Box
				sx={{
					p: 1.5,
					borderBottom: '1px solid',
					borderColor: 'divider',
					backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#fcfcfd' : 'action.hover'),
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
				}}
			>
				<FormControl sx={{ minWidth: 240, maxWidth: 400 }}>
					<InputLabel id="year-multiple-select-label">Compare by Year(s)</InputLabel>
					<Select
						labelId="year-multiple-select-label"
						id="year-multiple-select"
						multiple
						value={selectedYears}
						onChange={handleYearChange}
						input={<OutlinedInput label="Filter by Year(s)" />}
						// 2. Improved placeholder and selected value rendering
						renderValue={(selected) => {
							if (selected.length === 0) {
								return (
									<Typography variant="body2" color="text.secondary">
										Displaying Total (All Years)
									</Typography>
								);
							}
							return selected.join(', ');
						}}
						// 3. Enhanced styling for the dropdown menu
						MenuProps={{
							PaperProps: {
								sx: {
									maxHeight: 250,
									boxShadow: 3, // Apply a theme-aware shadow for elevation
								},
							},
						}}
					>
						{availableYears.map((year) => (
							<MenuItem key={year} value={year}>
								<Checkbox checked={selectedYears.indexOf(year) > -1} />
								<ListItemText primary={year} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{/* Comparison Table (no changes needed here) */}
			<TableContainer>
				<Table size="small">
					<TableHead
						// Style the table header to match the design
						sx={{
							'& .MuiTableCell-root': {
								backgroundColor: 'action.hover',
								fontWeight: 'bold',
							},
						}}
					>
						<TableRow>
							<TableCell>Account Title</TableCell>
							{displayData.map((data, index) => (
								<TableCell key={data.leaseId || index} align="right">
									Lease {index + 1}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell component="th" scope="row">
								Lease ID
							</TableCell>
							{displayData.map((data, index) => (
								<TableCell key={data.leaseId || index} align="right">
									{data.leaseId}
								</TableCell>
							))}
						</TableRow>
						{allAccountTitles.map((key) => (
							<TableRow key={key}>
								<TableCell component="th" scope="row">
									{key}
								</TableCell>
								{displayData.map((data, index) => (
									<TableCell key={data.leaseId || index} align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
										{(data[key] || 0).toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Fragment>
	);
};

export default LeaseComparisonTable;
